import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, errorMessage, post } from "@/lib/api";
import "./ClassAttendance.css";

/**
 * One course meeting. Remove this file, its stylesheet, the teaching routes,
 * courseAttendanceService, and the Attendance tab to take it back out.
 *
 * The day starts unmarked. Every change saves as soon as it is made:
 * - One click sets Present, Absent, Late, or Excused. Clicking the selected
 *   mark again (or Clear) takes it back off.
 * - Mark remaining present fills anyone still not taken.
 * - Late opens an arrival time (filled with the current time when the day is
 *   today). It saves when the field loses focus.
 * - A note saves when the field loses focus or on Enter.
 */

type Learner = {
  uisUgpId: string;
  person: { firstName?: string | null; lastName?: string | null; email?: string | null } | null;
};

type Mark = {
  uisUgpId: string;
  status: string | null;
  arrivedAt?: string | null;
  note?: string | null;
};

type Entry = { status: string | null; arrivedAt: string | null; note: string | null };

type Change = {
  uisUgpId: string;
  status?: string | null;
  arrivedAt?: string | null;
  note?: string | null;
};

type Draft = { note?: string; arrivedAt?: string };

const MARKS = [
  { value: "PRESENT", label: "Present" },
  { value: "ABSENT", label: "Absent" },
  { value: "LATE", label: "Late" },
  { value: "EXCUSED", label: "Excused" },
];

const NOTE_MAX = 500;

function learnerName(row: Learner) {
  const name = [row.person?.firstName, row.person?.lastName].filter(Boolean).join(" ").trim();
  return name || row.person?.email || "Student";
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}

function isoDate(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function today() {
  return isoDate(new Date());
}

function shiftDate(iso: string, days: number) {
  const [y, m, d] = iso.split("-").map(Number);
  return isoDate(new Date(y, m - 1, d + days));
}

function nowTime() {
  const now = new Date();
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function clockTime() {
  return new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function entriesFromRows(rows: Mark[] | undefined) {
  const next: Record<string, Entry> = {};
  for (const mark of rows || []) {
    next[mark.uisUgpId] = {
      status: mark.status || null,
      arrivedAt: mark.arrivedAt || null,
      note: mark.note || null,
    };
  }
  return next;
}

function applyChange(entry: Entry | undefined, change: Change): Entry {
  const next: Entry = { ...(entry || { status: null, arrivedAt: null, note: null }) };
  if ("status" in change) next.status = change.status ?? null;
  if ("arrivedAt" in change) next.arrivedAt = change.arrivedAt ?? null;
  if ("note" in change) next.note = change.note ?? null;
  if (next.status !== "LATE") next.arrivedAt = null;
  return next;
}

export default function ClassAttendance({
  courseId,
  learners,
}: {
  courseId: string;
  learners: Learner[];
}) {
  const [date, setDate] = useState(today);
  const [session, setSession] = useState(1);
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, number>>({});
  const [savingAll, setSavingAll] = useState(false);
  const [savedAt, setSavedAt] = useState("");
  const [error, setError] = useState("");
  const viewKey = useRef("");
  const dateId = useId();
  const sessionId = useId();

  const roster = useMemo(
    () =>
      [...learners].sort((a, b) =>
        learnerName(a).localeCompare(learnerName(b), undefined, { sensitivity: "base" }),
      ),
    [learners],
  );

  const unmarked = roster.filter((learner) => !entries[learner.uisUgpId]?.status);

  const counts = useMemo(() => {
    const tally: Record<string, number> = { PRESENT: 0, ABSENT: 0, LATE: 0, EXCUSED: 0 };
    let notMarked = 0;
    for (const learner of roster) {
      const status = entries[learner.uisUgpId]?.status;
      if (status && status in tally) tally[status] += 1;
      else notMarked += 1;
    }
    return [
      { label: "Enrolled", value: roster.length },
      ...MARKS.map((mark) => ({ label: mark.label, value: tally[mark.value] })),
      { label: "Not marked", value: notMarked },
    ];
  }, [roster, entries]);

  useEffect(() => {
    let cancel = false;
    const key = `${courseId}|${date}|${session}`;
    viewKey.current = key;
    setLoading(true);
    setError("");
    setDrafts({});
    setSavedAt("");
    const params = new URLSearchParams({ date, session: String(session) });
    api<{ success: boolean; data?: { marks?: Mark[] } }>(
      `/api/teaching/classes/${encodeURIComponent(courseId)}/attendance?${params}`,
    )
      .then((envelope) => {
        if (cancel) return;
        setEntries(entriesFromRows(envelope.data?.marks));
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [courseId, date, session]);

  function bump(ids: string[], by: number) {
    setSaving((current) => {
      const next = { ...current };
      for (const id of ids) {
        const count = (next[id] || 0) + by;
        if (count > 0) next[id] = count;
        else delete next[id];
      }
      return next;
    });
  }

  /** Saves right away. Returns true once the server has it. */
  async function save(changes: Change[], all = false) {
    const key = viewKey.current;
    const ids = changes.map((change) => change.uisUgpId);
    const previous: Record<string, Entry | undefined> = {};
    for (const id of ids) previous[id] = entries[id];

    setEntries((current) => {
      const next = { ...current };
      for (const change of changes) {
        next[change.uisUgpId] = applyChange(current[change.uisUgpId], change);
      }
      return next;
    });
    bump(ids, 1);
    if (all) setSavingAll(true);
    setError("");
    try {
      const envelope = await post<{ success: boolean; data?: { marks?: Mark[] } }>(
        `/api/teaching/classes/${encodeURIComponent(courseId)}/attendance`,
        { date, session, marks: changes },
      );
      if (viewKey.current !== key) return true;
      const fresh = entriesFromRows(envelope.data?.marks);
      setEntries((current) => {
        const next = { ...current };
        for (const id of ids) {
          if (fresh[id]) next[id] = fresh[id];
          else delete next[id];
        }
        return next;
      });
      setSavedAt(clockTime());
      return true;
    } catch (err) {
      if (viewKey.current !== key) return false;
      setEntries((current) => {
        const next = { ...current };
        for (const id of ids) {
          const before = previous[id];
          if (before) next[id] = before;
          else delete next[id];
        }
        return next;
      });
      setError(errorMessage(err));
      return false;
    } finally {
      bump(ids, -1);
      if (all) setSavingAll(false);
    }
  }

  function setDraft(uisUgpId: string, field: keyof Draft, value: string) {
    setDrafts((current) => ({ ...current, [uisUgpId]: { ...current[uisUgpId], [field]: value } }));
  }

  function clearDraft(uisUgpId: string, field: keyof Draft, ifValue?: string) {
    setDrafts((current) => {
      const row = current[uisUgpId];
      if (!row || !(field in row)) return current;
      if (ifValue !== undefined && row[field] !== ifValue) return current;
      const rest = { ...row };
      delete rest[field];
      const next = { ...current };
      if (Object.keys(rest).length) next[uisUgpId] = rest;
      else delete next[uisUgpId];
      return next;
    });
  }

  function setMark(uisUgpId: string, status: string) {
    const entry = entries[uisUgpId];
    const next = entry?.status === status ? null : status;
    const change: Change = { uisUgpId, status: next };
    if (next === "LATE" && !entry?.arrivedAt && date === today()) {
      change.arrivedAt = nowTime();
    }
    clearDraft(uisUgpId, "arrivedAt");
    void save([change]);
  }

  async function commitField(uisUgpId: string, field: keyof Draft) {
    const draft = drafts[uisUgpId]?.[field];
    if (draft === undefined) return;
    const value = draft.trim();
    if (value === (entries[uisUgpId]?.[field] || "")) {
      clearDraft(uisUgpId, field);
      return;
    }
    const ok = await save([{ uisUgpId, [field]: value || null }]);
    if (ok) clearDraft(uisUgpId, field, draft);
  }

  function markRemainingPresent() {
    if (!unmarked.length || savingAll) return;
    void save(
      unmarked.map((learner) => ({ uisUgpId: learner.uisUgpId, status: "PRESENT" })),
      true,
    );
  }

  const anySaving = Object.keys(saving).length > 0;
  const statusLine = anySaving
    ? "Saving…"
    : savedAt
      ? `All changes saved at ${savedAt}.`
      : "Changes save as you make them.";

  return (
    <section className="al-class-panel al-attend" id="attendance">
      <div className="al-attend-bar">
        <div className="al-attend-field">
          <label htmlFor={dateId}>Date</label>
          <div className="al-attend-stepper">
            <button
              type="button"
              aria-label="Previous day"
              onClick={() => setDate((current) => shiftDate(current, -1))}
            >
              <ChevronLeft size={16} aria-hidden="true" />
            </button>
            <input
              id={dateId}
              type="date"
              value={date}
              onChange={(event) => {
                if (event.target.value) setDate(event.target.value);
              }}
            />
            <button
              type="button"
              aria-label="Next day"
              onClick={() => setDate((current) => shiftDate(current, 1))}
            >
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="al-attend-field">
          <label htmlFor={sessionId}>Session</label>
          <input
            id={sessionId}
            className="al-attend-session"
            type="number"
            min={1}
            step={1}
            value={session}
            onChange={(event) => {
              const next = Number(event.target.value);
              setSession(Number.isInteger(next) && next >= 1 ? next : 1);
            }}
          />
        </div>
        <button
          type="button"
          className="al-attend-all"
          disabled={loading || savingAll || unmarked.length === 0}
          onClick={markRemainingPresent}
        >
          Mark remaining present
        </button>
      </div>
      <p className="al-attend-note">
        Click the selected mark again to clear it. Session 2 is a second meeting on the same day.
      </p>

      {error ? (
        <p className="al-attend-error" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? <p className="al-attend-note">Loading…</p> : null}

      {!loading && roster.length === 0 ? (
        <p className="al-class-empty">No students are enrolled.</p>
      ) : null}

      {!loading && roster.length > 0 ? (
        <>
          <dl className="al-attend-counts">
            {counts.map((count) => (
              <div key={count.label}>
                <dt>{count.label}</dt>
                <dd>{count.value}</dd>
              </div>
            ))}
          </dl>

          <div className="al-attend-scroll">
            <table className="al-attend-table">
              <caption className="al-attend-sr">
                Attendance for {date}, session {session}
              </caption>
              <thead>
                <tr>
                  <th scope="col" className="al-attend-student">
                    Student
                  </th>
                  {MARKS.map((mark) => (
                    <th scope="col" key={mark.value} className="al-attend-mark">
                      {mark.label}
                    </th>
                  ))}
                  <th scope="col" className="al-attend-arrived">
                    Arrived
                  </th>
                  <th scope="col" className="al-attend-notecol">
                    Note
                  </th>
                  <th scope="col" className="al-attend-clear">
                    <span className="al-attend-sr">Clear mark</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {roster.map((learner) => {
                  const id = learner.uisUgpId;
                  const name = learnerName(learner);
                  const entry = entries[id];
                  const status = entry?.status || "";
                  const rowBusy = Boolean(saving[id]) || savingAll;
                  const arrived = drafts[id]?.arrivedAt ?? entry?.arrivedAt ?? "";
                  const note = drafts[id]?.note ?? entry?.note ?? "";
                  return (
                    <tr key={id} className={status ? "" : "is-unmarked"}>
                      <th scope="row" className="al-attend-student">
                        <span className="al-attend-name">{name}</span>
                        {status ? null : <span className="al-attend-pending">Not marked</span>}
                      </th>
                      {MARKS.map((mark) => (
                        <td key={mark.value} className="al-attend-mark">
                          <label className="al-attend-radio">
                            <input
                              type="radio"
                              name={`attend-${id}`}
                              value={mark.value}
                              checked={status === mark.value}
                              disabled={rowBusy}
                              aria-label={`${mark.label}, ${name}`}
                              onChange={() => undefined}
                              onClick={() => setMark(id, mark.value)}
                            />
                          </label>
                        </td>
                      ))}
                      <td className="al-attend-arrived">
                        {status === "LATE" ? (
                          <input
                            type="time"
                            aria-label={`Arrival time, ${name}`}
                            value={arrived}
                            onChange={(event) => setDraft(id, "arrivedAt", event.target.value)}
                            onBlur={() => void commitField(id, "arrivedAt")}
                          />
                        ) : (
                          <span className="al-attend-dash" aria-hidden="true">
                            —
                          </span>
                        )}
                      </td>
                      <td className="al-attend-notecol">
                        <input
                          type="text"
                          aria-label={`Note, ${name}`}
                          placeholder="Add a note"
                          maxLength={NOTE_MAX}
                          value={note}
                          onChange={(event) => setDraft(id, "note", event.target.value)}
                          onBlur={() => void commitField(id, "note")}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") event.currentTarget.blur();
                          }}
                        />
                      </td>
                      <td className="al-attend-clear">
                        {status ? (
                          <button
                            type="button"
                            disabled={rowBusy}
                            aria-label={`Clear mark, ${name}`}
                            onClick={() => setMark(id, status)}
                          >
                            Clear
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="al-attend-status" aria-live="polite">
            {statusLine}
          </p>
        </>
      ) : null}
    </section>
  );
}
